import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import React, { useEffect, useMemo } from 'react';
import {
  CloseIconImage,
  StyledAlertDialogOverlay,
  StyledAlertDialogContent,
} from '@/features/auth-managment/styles';
import { Wrapper, FormGroup, FormGroupItem, FormGroupButton } from './styles';
import {
  HeadingMediumH5,
  BodyTextMediumB,
  BodyTextMediumC,
  BodyTextRegularC,
} from '@/shared/ui/typography2';
import { useSearchParams } from 'next/navigation';
import { Condition } from '@/shared/ui/condition';
import { Button } from '@/shared/ui/form/button';
import { PhoneField } from '@/shared/ui/form/phone-field';
import { Box } from '@/shared/ui/box';
import { useI18n } from '@locales/client';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { Form, Field } from 'react-final-form';
import { FormField } from '@/shared/ui/form/form-field';
import { usePhoneMasks } from '@/entities/phone-mask/model/hooks';
import { validateSchema } from '@/shared/libs/validate';
import { useConfirmDataPrompt } from '@/features/data-managment/model/hooks';
import { useConfirmApi } from '@/features/data-managment/model/api';
import {
  PhoneSchema,
  EmailSchema,
  VerificationSchema,
} from '@/features/data-managment/model/schemas';

const ModalPrompts = () => {
  const {
    showPopup,
    onPopupShow,
    onPopupClose,
    addQueryParam,
    role,
    removeQueryParam,
  } = useConfirmDataPrompt();

  const { confirmTriggerEmail, confirmTriggerPhone } = useConfirmApi();
  const [currentCountry, setCurrentCountry] = React.useState({});
  const { user } = useUser();
  const t = useI18n();
  const searchParams = useSearchParams();
  const isConfirmData = searchParams.get('modal') === 'confirm';
  const triggerFromProfileParam = searchParams.get('triggerFromProfile');
  const confirmationCodeParam = searchParams.get('confirmationCode');

  const [form, setForm] = React.useState({
    email: '',
    phone: '',
  });

  const {
    phoneMasks,
    isLoading: isPhoneMasksLoading,
    isError,
  } = usePhoneMasks();

  useEffect(() => {
    if (!isPhoneMasksLoading) {
      setCurrentCountry(
        phoneMasks.find((mask) => mask.default) || phoneMasks[0]
      );
    }
  }, [isPhoneMasksLoading]);

  const handleClose = () => {
    onPopupClose();
  };

  const handleOpen = () => {
    onPopupShow();
  };

  const PhoneFieldCmp = (props) => {
    return (
      <PhoneField
        country={currentCountry}
        onCountryChange={setCurrentCountry}
        {...props}
      />
    );
  };

  useEffect(() => {
    if ((showPopup || isConfirmData) && role !== 'player' && !user?.telegram) {
      handleOpen();
    }
  }, []);

  const onSubmit = async () => {
    handleClose();
    addQueryParam('verification');

    try {
      if (user?.phone && !user?.phoneConfirm) {
        await confirmTriggerPhone();
      } else if (user?.email && !user?.emailConfirm) {
        await confirmTriggerEmail();
      }
    } catch (error) {
      console.error('Ошибка подтверждения:', error);
    }
  };

  const getSchema = useMemo(() => {
    if (!user?.id) {
      return undefined;
    }

    const hasPhone = Boolean(user?.phone?.value || user?.phone);
    const hasEmail = Boolean(user?.email);
    let schema;

    if (!hasPhone && !hasEmail) {
      return undefined;
    }

    if (hasPhone && hasEmail) {
      schema = VerificationSchema(t);
    } else if (hasEmail) {
      schema = EmailSchema(t);
    } else {
      schema = PhoneSchema(t);
    }

    return validateSchema(schema);
  }, [user, t]);

  useEffect(() => {
    if (user?.email || user?.phone) {
      setForm({
        email: user?.email || '',
        phone: user?.phone?.value || '',
      });
    }
  }, [user]);

  const isShowModal = useMemo(() => {
    if (triggerFromProfileParam && confirmationCodeParam) {
      handleClose();
      return false;
    }
    if (
      !(
        (user?.email && !user?.emailConfirm) ||
        (user?.phone && !user?.phoneConfirm)
      )
    ) {
      return false;
    }

    if (user?.telegram) {
      return false;
    }

    return (
      !(user?.phoneConfirm && user?.emailConfirm) &&
      (showPopup || isConfirmData)
    );
  }, [
    showPopup,
    isConfirmData,
    user?.telegram,
    user?.phoneConfirm,
    user?.emailConfirm,
    triggerFromProfileParam,
    confirmationCodeParam,
  ]);

  const getHeight = useMemo(() => {
    if (!user) return;
    const { email, emailConfirm, phone, phoneConfirm } = user;

    const isEmailPending = email && !emailConfirm;
    const isPhonePending = phone && !phoneConfirm;

    if (isEmailPending && isPhonePending) {
      return '630px';
    }
    if (isEmailPending || isPhonePending) {
      return '490px';
    }
  }, [user]);

  useEffect(() => {
    if (!isShowModal) {
      removeQueryParam('modal');
    }
  }, [isShowModal, isConfirmData]);

  return (
    <Dialog.Root open={isShowModal} onOpenChange={handleOpen}>
      <Dialog.Portal>
        <VisuallyHidden>
          <Dialog.Title>DataManagment</Dialog.Title>
        </VisuallyHidden>
        <StyledAlertDialogOverlay onClick={handleClose} />
        <StyledAlertDialogContent>
          <Wrapper $height={getHeight}>
            <Box onClick={handleClose}>
              <CloseIconImage
                width={32}
                height={32}
                src="/images/icons/close-dialog.svg"
              />
            </Box>
            <Form
              validate={getSchema}
              onSubmit={onSubmit}
              initialValues={form}
              render={({ handleSubmit, submitting, pristine, ...props }) => (
                <FormGroup onSubmit={handleSubmit}>
                  <FormGroupItem>
                    <Box $textAlign="center">
                      <HeadingMediumH5>
                        {t('account_verification.accountDataVerification')}
                      </HeadingMediumH5>
                    </Box>

                    <Condition match={user?.email && !user?.emailConfirm}>
                      <Field
                        name={'email'}
                        autoComplete="off"
                        required
                        placeholder={t('auth.login.email.placeholder')}
                        component={FormField}
                        type={'text'}
                        disabled
                        label={
                          <BodyTextMediumB>
                            {t('auth.login.email.label')}
                          </BodyTextMediumB>
                        }
                      />
                      <BodyTextRegularC color="#AFAFAF">
                        {t('account_verification.emailVerification')}
                      </BodyTextRegularC>
                    </Condition>
                    <Condition match={user?.phone && !user?.phoneConfirm}>
                      <Field
                        name={'phone'}
                        disabled
                        required
                        autoComplete="off"
                        component={PhoneFieldCmp}
                        type={'text'}
                        label={
                          <BodyTextMediumB>
                            {t('auth.login.phone.label')}
                          </BodyTextMediumB>
                        }
                      />
                      <BodyTextRegularC color="#AFAFAF">
                        {t('account_verification.smsVerification')}
                      </BodyTextRegularC>
                    </Condition>
                  </FormGroupItem>
                  <FormGroupButton>
                    <Button
                      type="submit"
                      variant="orangeGradient"
                      disabled={submitting || props.invalid}
                    >
                      <BodyTextMediumC>
                        {t('account_verification.confirm')}
                      </BodyTextMediumC>
                    </Button>
                    <Button type="button" variant="gray" onClick={handleClose}>
                      <BodyTextMediumC>
                        {t('account_verification.doLater')}
                      </BodyTextMediumC>
                    </Button>
                  </FormGroupButton>
                </FormGroup>
              )}
            />
          </Wrapper>
        </StyledAlertDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export { ModalPrompts };
